import * as React from 'react';
import { WorkplaceAssessmentComponentProps } from './WorkplaceAssessmentComponentProps';
import { SegmentReport, SurveySegment, sortSegmentLists, surveyHelper, AggregatedSurveyReport, segmentListIsInLists, surveySegmentCompare, surveySegmentListCompare } from '../../../amorg-common';
import { getSurveySegmentReport } from '../../../api/surveyClientApi';
import { P } from '../../../designSystem/typography/Body';
import { EditableSegmentPanel } from '../../surveys/segmentation/EditableSegmentPanel';
import { SurveyReportLinkPanel } from '../../surveys/report/SurveyReportLinkPanel';
import { SurveyReportPanel } from '../../surveys/report/SurveyReportPanel';
import { ToolBarButton, ToolBarContainer } from '../../../designSystem/compositeComponents/ButtonToolBar';
import { H2 } from '../../../designSystem/typography/Heading';
import { useReactToPrint } from 'react-to-print';
import { PageHeader } from '../../../designSystem/PageHeader';
import { PageSection } from '../../../designSystem/PageSection';
import PrintSurveyReportPage from '../../../pages/PrintSurveyReportPage';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { amorgLocalStorage } from '../../../utils/amorgLocalStorage';
import clone from 'clone';
import { TextAvatar } from '../../../designSystem/avatar/TextAvatar';


type PageState = {
    segmentReports:SegmentReport[],
    segmentFilter: SurveySegment[][]
};

export function ReportPanel(props:WorkplaceAssessmentComponentProps): JSX.Element 
{
    const [state, setState] = React.useState<PageState>({
        segmentReports:[],
        segmentFilter: []
    });

    React.useEffect(() => {
        const getData = async () => {

            const clonedLists = clone(await getSurveySegmentReport(props.survey.id));
            for(const sr of clonedLists){
                sr.segments.sort((a, b) => surveySegmentCompare(props.survey.segmentations, a, b));
            }
            clonedLists.sort((a, b) => surveySegmentListCompare(props.survey.segmentations, a.segments, b.segments));

            let segmentFilter:SurveySegment[][] = [];
            if(props.survey.segmentations.length > 0)
            {
                const allSegments = clone(clonedLists.map(sr => sr.segments));

                const storedFilterKey = amorgLocalStorage.surveySegmentation.filter.getKey('survey-report', props.survey.id);
                segmentFilter = amorgLocalStorage.surveySegmentation.filter.read(storedFilterKey);
                if(segmentFilter.length === 0)
                {
                    segmentFilter = allSegments;
                }
                else {
                    segmentFilter = segmentFilter.filter(sf => segmentListIsInLists(allSegments, sf));
                }
                sortSegmentLists(segmentFilter);
                amorgLocalStorage.surveySegmentation.filter.write(storedFilterKey, segmentFilter);
            }

            const newState:PageState = {
                segmentReports:clonedLists,
                segmentFilter
            };
            setState(newState);
        };
        getData();
    }, []);

    function handleFilterChanged(filter: SurveySegment[][]) {
        setState({
            ...state,
            segmentFilter:filter
        });
        const filterKey = amorgLocalStorage.surveySegmentation.filter.getKey('survey-report', props.survey.id);
        amorgLocalStorage.surveySegmentation.filter.write(filterKey, filter);
    }

    const printRef = React.useRef(null);
    const printFn = useReactToPrint({
        contentRef: printRef,
        documentTitle: "Rapport"
      });

    const hasLoaded = props.survey && props.surveyReport;
    const hasSegments = hasLoaded && props.survey.segmentations.length > 0;
    const hasQuestions = hasLoaded && surveyHelper.getAllSurveyQuestions(props.survey).length > 0;
    const combinedReport = hasLoaded
        ? new AggregatedSurveyReport(props.survey, props.surveyReport, state.segmentFilter).data
        : [];

    return (
        <>
        
        {hasLoaded && !hasQuestions &&
            <P>Spørgeskemaet indeholder ikke spørgsmål</P>
        }

        {hasLoaded && hasQuestions &&
        <>
            <PageHeader
                headerText='Rapport'
                headerStyle='secondary-gray'
                buttons={
                    <ToolBarContainer>      
                        <ToolBarButton
                            icon='interface_actions_print'
                            tooltip= 'Print'
                            variant= 'text'
                            onClick={printFn}
                        />

                        

                    </ToolBarContainer>
                } />

            <PrintSurveyReportPage
                innerRef={printRef}
                survey={props.survey}
                questionStats={combinedReport}
                segmentFilter={state.segmentFilter} 
                />
            
            {hasSegments &&
            <PageSection
                header={
                    <Flex gap={8} alignItems='center'>
                        <H2>Valgte segmenter</H2>
                        <TextAvatar
                            size='24px'
                            text={state.segmentFilter.length.toString()}
                        />
                    </Flex>
                }
                defaultCollapsed={false}>
                    <EditableSegmentPanel
                        wrapInCard
                        displayAnswerCount
                        localStorageViewModeKey={amorgLocalStorage.surveySegmentation.viewMode.getKey('survey-report', props.survey.id)}
                        segmentLists={state.segmentReports}
                        selection={state.segmentFilter}
                        onSelectionChange={handleFilterChanged}
                    />
            </PageSection>      
            }

            {props.activeUser && props.survey && 
                <SurveyReportLinkPanel siteId={props.activeUser.siteId} surveyId={props.survey.id} segmentFilter={state.segmentFilter} />
            }

            <SurveyReportPanel
                siteId={props.activeUser.siteId}
                survey={props.survey}
                questionStats={combinedReport}
                segmentFilter={state.segmentFilter}
                showComments={true} />
        </>}
        </>    
    );
}